const DEFAULT_GROUP = "Presets";

import iconAurora from "@/images/animated-bg-icons/anim-aurora.png";
import iconLightRays from "@/images/animated-bg-icons/anim-light-rays.png";
import iconBeams from "@/images/animated-bg-icons/anim-beams.png";
import iconThreads from "@/images/animated-bg-icons/anim-threads.png";
import iconLineWaves from "@/images/animated-bg-icons/anim-line-waves.png";
import iconParticles from "@/images/animated-bg-icons/anim-particles.png";
import iconDotGrid from "@/images/animated-bg-icons/anim-dot-grid.png";
import iconSquares from "@/images/animated-bg-icons/anim-squares.png";
import iconPixelBlast from "@/images/animated-bg-icons/anim-pixel-blast.png";
import iconGradient from "@/images/animated-bg-icons/anim-grainient.png";
import iconGradientBlinds from "@/images/animated-bg-icons/anim-gradient-blinds.png";
import iconSilk from "@/images/animated-bg-icons/anim-silk.png";

const preset = (id, title, rendererKey, baseProps, colorSlots = [], icon = "") => ({
    id,
    group: DEFAULT_GROUP,
    title,
    rendererKey,
    icon,
    baseProps,
    colorSlots
});

const colorSlot = (_id, label, propPath, color = null, paletteColorIndex = null) => ({
    _id,
    label,
    propPath,
    defaultColor: {
        color,
        paletteColorIndex
    }
});

export const ANIMATED_BACKGROUND_PRESETS = Object.freeze([
    preset(
        "aurora_default",
        "Aurora",
        "AURORA",
        {
            colorStops: ["#7cff67", "#171D22", "#7cff67"],
            amplitude: 1,
            blend: 0.5,
            speed: 0.5,
            intensity: 0.5
        },
        [
            colorSlot("aurora_1", "Color 1", "colorStops[0]", null, 4),
            colorSlot("aurora_2", "Color 2", "colorStops[1]", null, 2),
            colorSlot("aurora_3", "Color 3", "colorStops[2]", null, 2)
        ],
        iconAurora
    ),
    preset(
        "light_rays_default",
        "Light Rays",
        "LIGHT_RAYS",
        {
            raysOrigin: "top-center",
            raysColor: "#ffffff",
            raysSpeed: 0.6,
            lightSpread: 1,
            rayLength: 1,
            pulsating: false,
            fadeDistance: 1,
            saturation: 1,
            followMouse: true,
            mouseInfluence: 0.1,
            noiseAmount: 0,
            distortion: 0
        },
        [colorSlot("light_rays_color", "Rays Color", "raysColor", null, 0)],
        iconLightRays
    ),
    preset(
        "beams_default",
        "Beams",
        "BEAMS",
        {
            beamWidth: 2,
            beamHeight: 15,
            beamNumber: 12,
            lightColor: "#ffffff",
            speed: 1.45,
            noiseIntensity: 1.75,
            scale: 0.2,
            rotation: 0
        },
        [colorSlot("beams_color", "Light Color", "lightColor", null, 0)],
        iconBeams
    ),
    preset(
        "threads_default",
        "Threads",
        "THREADS",
        {
            color: [1, 1, 1],
            amplitude: 1,
            distance: 0,
            verticalPosition: 0.22,
            enableMouseInteraction: false
        },
        [colorSlot("threads_color", "Thread Color", "color", null, 0)],
        iconThreads
    ),
    preset(
        "line_waves_default",
        "Line Waves",
        "LINE_WAVES",
        {
            speed: 0.1,
            innerLineCount: 32,
            outerLineCount: 36,
            warpIntensity: 1,
            rotation: -45,
            edgeFadeWidth: 0,
            colorCycleSpeed: 1,
            brightness: 0.12,
            enableMouseInteraction: false,
            mouseInfluence: 2,
            color1: "#ffffff",
            color2: "#ffffff",
            color3: "#ffffff"
        },
        [
            colorSlot("line_waves_1", "Color 1", "color1", null, 4),
            colorSlot("line_waves_2", "Color 2", "color2", null, 3),
            colorSlot("line_waves_3", "Color 3", "color3", null, 0)
        ],
        iconLineWaves
    ),
    preset(
        "particles_default",
        "Particles",
        "PARTICLES",
        {
            particleCount: 170,
            particleSpread: 11,
            speed: 0.1,
            particleColors: ["#ffffff", "#ffffff", "#ffffff"],
            moveParticlesOnHover: false,
            particleHoverFactor: 1,
            alphaParticles: false,
            disableRotation: false,
            cameraDistance: 20,
            sizeRandomness: 1,
            particleBaseSize: 100
        },
        [
            colorSlot("particles_1", "Color 1", "particleColors[0]", null, 0),
            colorSlot("particles_2", "Color 2", "particleColors[1]", null, 0),
            colorSlot("particles_3", "Color 3", "particleColors[2]", null, 2)
        ],
        iconParticles
    ),
    preset(
        "dot_grid_default",
        "Dot Grid",
        "DOT_GRID",
        {
            dotSize: 2,
            gap: 18,
            baseColor: "#27FF64",
            activeColor: "#27FF64",
            proximity: 150,
            speedTrigger: 100,
            shockRadius: 185,
            shockStrength: 3,
            maxSpeed: 4500,
            resistance: 530,
            returnDuration: 1.5
        },
        [
            colorSlot("dot_grid_base", "Base Color", "baseColor", null, 3),
            colorSlot("dot_grid_active", "Active Color", "activeColor", null, 0)
        ],
        iconDotGrid
    ),
    preset(
        "squares_default",
        "Squares",
        "SQUARES",
        {
            direction: "diagonal",
            speed: 0,
            borderColor: "#999999",
            squareSize: 40,
            hoverFillColor: "#222222",
            edgeShadowEnabled: false,
            edgeShadowColor: "#0b0b0b"
        },
        [
            colorSlot("squares_border", "Border Color", "borderColor", null, 3),
            colorSlot("squares_fill", "Hover Fill", "hoverFillColor", null, 2)
        ],
        iconSquares
    ),
    preset(
        "pixel_blast_default",
        "Pixel Blast",
        "PIXEL_BLAST",
        {
            variant: "square",
            pixelSize: 3,
            color: "#B19EEF",
            antialias: true,
            patternScale: 1.8,
            patternDensity: 1,
            liquid: false,
            liquidStrength: 0.1,
            liquidRadius: 1,
            pixelSizeJitter: 0,
            enableRipples: true,
            rippleIntensityScale: 1.5,
            rippleThickness: 0.1,
            rippleSpeed: 0.3,
            liquidWobbleSpeed: 4.5,
            autoPauseOffscreen: true,
            speed: 0.5,
            transparent: true,
            edgeFade: 0.5,
            noiseAmount: 0
        },
        [colorSlot("pixel_blast_color", "Color", "color", null, 1)],
        iconPixelBlast
    ),
    preset(
        "grainient_default",
        "Grainient",
        "GRAINIENT",
        {
            timeSpeed: 0.25,
            colorBalance: 0.0,
            warpStrength: 1.0,
            warpFrequency: 5.0,
            warpSpeed: 2.0,
            warpAmplitude: 50.0,
            blendAngle: 0.0,
            blendSoftness: 0.05,
            rotationAmount: 500.0,
            noiseScale: 2.0,
            grainAmount: 0.1,
            grainScale: 2.0,
            grainAnimated: false,
            contrast: 1.5,
            gamma: 1.0,
            saturation: 1.0,
            centerX: 0.0,
            centerY: 0.0,
            zoom: 0.9,
            color1: "#FF9FFC",
            color2: "#5227FF",
            color3: "#B19EEF"
        },
        [
            colorSlot("gradient_1", "Color 1", "color1", null, 3),
            colorSlot("gradient_2", "Color 2", "color2", null, 2),
            colorSlot("gradient_3", "Color 3", "color3", null, 2)
        ],
        iconGradient
    ),
    preset(
        "gradient_blinds_default",
        "Gradient Blinds",
        "GRADIENT_BLINDS",
        {
            paused: false,
            angle: 25,
            noise: 0.3,
            blindCount: 16,
            blindMinWidth: 60,
            mouseDampening: 0.15,
            mirrorGradient: false,
            spotlightRadius: 0.5,
            spotlightSoftness: 1,
            spotlightOpacity: 1,
            distortAmount: 0,
            shineDirection: "left",
            mixBlendMode: "soft-light",
            gradientColors: ["#FF9FFC", "#27FF64", "#B19EEF"]
        },
        [
            colorSlot("gradient_blinds_1", "Color 1", "gradientColors[0]", null, 1),
            colorSlot("gradient_blinds_2", "Color 2", "gradientColors[1]", null, 1),
            colorSlot("gradient_blinds_3", "Color 3", "gradientColors[2]", null, 1)
        ],
        iconGradientBlinds
    ),
    preset(
        "silk_default",
        "Silk",
        "SILK",
        {
            speed: 2.5,
            scale: 1,
            color: "#7B7481",
            noiseIntensity: 1.5,
            rotation: 0
        },
        [colorSlot("silk_color", "Color", "color", null, 1)],
        iconSilk
    )
]);
